import React, {useEffect, useRef} from 'react';

import {Box} from '@mui/material';

import {useDroppable} from '@dnd-kit/core';

import {BANNER_TIMEOUT} from 'config';
import {PencilBrush} from 'fabric';
import {useTimer} from 'hooks';
import {useBannersStore} from 'module/banner';
import {useLockerRoomStore} from 'module/lockerRoom/zustand';
import {PlayerDialog, usePlayerStore} from 'module/player';
import {useSheet} from 'module/sheet/useSheet';
import {TextDialog, useTextStore} from 'module/text';
import {useLayoutStore,usePrefsStore, useSnackbarStore} from 'store';

import {clearEvents, clearObjectEvents, registerGlobalEvents, registerObjectEvents} from './canvas-handlers';
import {
    canvasToJson,
    DEFAULT_COLOR, discardActiveObject, DRAWING_WIDTH, limitReached, setFabricDefaults,
    useCalculateDimensions,
} from './canvas-helper';
import {setScaling} from './object-helper';
import useCustomControls, {customControlVisibility, setGroupControlVisibility, syncCustomCoords} from './useCustomControls';
import useFabric from './useFabric';
import useMode from './useMode';
import {useCanvasStore} from './zustand';

const styles = {
    canvas: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
    },
    canvasContainer: {
        position: 'relative',
        minHeight: '400px',
        transition: 'background-color 0.2s ease',
    },
    canvasContainerDragOver: {
        backgroundColor: 'rgba(0, 255, 0, 0.1)',
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '2px dashed rgba(0, 255, 0, 0.5)',
            borderRadius: '8px',
            pointerEvents: 'none',
        }
    },
    // dataImage: {
    //     position: 'absolute',
    //     top: 0,
    //     left: 0,
    // },
    // dataImageBox: {
    //     display: 'flex',
    //     flex: 'none',
    // }
};

const Canvas = ({isMobile}) => {
    const canvas = useCanvasStore(state => state.canvas);

    const setMode = useMode();

    // locker room state, to be set intially by APP/pushNots and later by socketIO server
    const editor = useLockerRoomStore((state) => state.editor);
    // const dataImage = useLockerRoomStore((state) => state.dataImage);

    const setTool = usePrefsStore(state => state.setTool);

    // edit
    const setPlayer = usePlayerStore(state => state.set);
    const player = usePlayerStore(state => state.player);
    const setText = useTextStore(state => state.set);
    const text = useTextStore(state => state.text);

    const showInfo = useSnackbarStore(state => state.show);

    const setCanvas = useCanvasStore(state => state.set);
    const setCanvasInit = useCanvasStore(state => state.setCanvasInit);
    const setDirty = useCanvasStore(state => state.setDirty);
    const pushCanvas = useCanvasStore(state => state.pushCanvas);
    const getCanvasStates = useCanvasStore(state => state.getCanvasStates);

    // Use the lineup dragging state from the layout store
    const lineupIsDragging = useLayoutStore((state) => state.lineupIsDragging);

    const {frameIndex} = useSheet();
    const editable = frameIndex === 0;

    const bannersShow = useBannersStore(store => store.show);

    const {setNodeRef, isOver} = useDroppable({
        id: 'canvas-container',
    });
    // custom controls
    const {updateControls} = useCustomControls(canvas);

    if (canvas) {
        //console.log('canvas w/h', canvas.width, canvas.height);
    }

    const dimensions = useCalculateDimensions(canvas);

    const fabricRef = useFabric(fabricCanvas => {
        setFabricDefaults(fabricCanvas);
        fabricCanvas.freeDrawingBrush = new PencilBrush(fabricCanvas);
        fabricCanvas.freeDrawingBrush.color = DEFAULT_COLOR;
        fabricCanvas.freeDrawingBrush.width = DRAWING_WIDTH;
        setCanvas(fabricCanvas);
        setCanvasInit(true);
    }, {
        // isDrawingMode: mode === 'draw',
        isDrawingMode: true,
        width: dimensions.width,
        height: dimensions.height
    });

    const [resetBannerTimer] = useTimer(() => {
        !isMobile && bannersShow();
    }, BANNER_TIMEOUT * 1000); // 600 seconds

    // Track if user is currently interacting with an object
    const isInteractingRef = useRef(false);

    const [resetControlHideTimer] = useTimer(() => {
        if (!canvas) {
            return;
        }

        // Don't auto-deselect if user is actively interacting
        if (isInteractingRef.current) {
            resetControlHideTimer();
            return;
        }

        if (!player && !text) {
            // remove selection if nothing is in edit mode
            // check for canvas fast refresh during dev
            // warning: this triggers 'selection:updated' and 'before:selection:cleared'
            discardActiveObject(canvas);
        } else {
            // start again if player or text edit is open
            resetControlHideTimer();
        }
    }, 5 * 1000); // 5 seconds

    // we need to call this after every call of clearObjectEvents
    // const activateMode = () => {
    // setMode(canvas, editable ? mode : SELECTION_MODE, tool, color);
    // };

    // Android WebView: re-render canvas after app resume (visibility restored)
    useEffect(() => {
        if (!canvas) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                canvas.requestRenderAll();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [canvas]);

    // reset inactivity timer on every render
    useEffect(() => {
        resetControlHideTimer();
        resetBannerTimer();
    });

    useEffect(() => {
        // during initialization canvas is null
        if (canvas === null) {
            return;
        }

        const onChange = (_e) => {
            if (canvas.isLoading) {
                return;
            }

            setDirty(true);
            // Use requestAnimationFrame to ensure the canvas state is saved after the current render cycle
            requestAnimationFrame(() => {
                pushCanvas(canvasToJson(canvas));
            });
            resetControlHideTimer();
        };

        const onAdded = (evt) => {
            if (canvas.isLoading) {
                return;
            }

            setScaling(evt.target, isMobile);

            if (evt.target?.custom?.type === 'text') {
                setTool('select'); // #10
                setText(evt.target);
            }

            if (evt.target?.custom?.type === 'clipart') {
                if (evt.target.custom?.clipart === 'futbol') {
                    setTool('select'); // #6
                }
            }

            if (limitReached(canvas)) {
                showInfo('board.layout.validation.max_items', {severity: 'warning'});
            }

            customControlVisibility(evt.target);
            setDirty(true);
            pushCanvas(canvasToJson(canvas));
            resetControlHideTimer();
        };

        // Track interaction events
        const handleInteractionStart = () => {
            isInteractingRef.current = true;
        };

        const handleInteractionEnd = () => {
            isInteractingRef.current = false;
            resetControlHideTimer();
        };

        clearEvents(canvas);
        registerGlobalEvents(canvas, onChange, onAdded, onChange);
        
        // Register interaction tracking events
        canvas.on('object:moving', (e) => {
            handleInteractionStart();
            syncCustomCoords(e.target);
        });
        canvas.on('object:scaling', handleInteractionStart);
        canvas.on('object:rotating', (e) => {
            handleInteractionStart();
            syncCustomCoords(e.target);
        });
        canvas.on('object:modified', (e) => {
            handleInteractionEnd();
            syncCustomCoords(e.target);
        });
        canvas.on('mouse:up', handleInteractionEnd);
        canvas.on('mouse:down:before', (e) => {
            // Mark interaction start when clicking a control handle
            if (e.target) {
                handleInteractionStart();
            }
        });
        
        canvas.on({
            'mouse:dblclick': (e) => {
                const target = e.target;
                if (target?.custom?.type === 'player') {
                    setPlayer(target);
                }
            },
            'selection:created': (e) => {
                const selected = e.selected?.[0];

                if (Array.isArray(e.selected) && e.selected.length > 0) {
                    // its group
                    if (e.selected.length > 1) {
                        setGroupControlVisibility(canvas.getActiveObject());
                    }
                }

                if (selected) {
                    customControlVisibility(selected);
                    registerObjectEvents(canvas, selected);
                    setMode(editable);
                }

                resetControlHideTimer();
            },
            'selection:updated': (e) => {
                const selected = e.selected?.[0];

                if (Array.isArray(e.selected) && e.selected.length > 0) {
                    // its group
                    if (e.selected.length > 1) {
                        setGroupControlVisibility(canvas.getActiveObject());
                    }
                }

                if (selected) {
                    customControlVisibility(selected);
                    clearObjectEvents(canvas);
                    registerObjectEvents(canvas, selected);
                    registerGlobalEvents(canvas, onChange, onAdded, onChange);
                    setMode(editable);
                }

                resetControlHideTimer();
            },
            'selection:cleared': (e) => {
                clearObjectEvents(canvas);
                registerGlobalEvents(canvas, onChange, onAdded, onChange);
                setMode(editable);
                setPlayer(null);
                setText(null);

                if (e.deselected && e.deselected[0] && e.deselected[0].text === '') {
                    canvas.remove(e.deselected[0]);
                }

                resetControlHideTimer();
            },
        });

        setMode(editable, true);

        // update event handler if needed
        updateControls();
        resetControlHideTimer();

        // }, [canvas, editor, editable, tool, mode, color, setPlayer, setText, setDirty, setTool, showInfo, isMobile, getCanvasStates, pushCanvas]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, editor, editable, setPlayer, setText, setDirty, setTool, showInfo, isMobile, getCanvasStates, pushCanvas, setMode]);
    // console.log('DATAIMAGE', !!dataImage);
    return <Box 
        id={'canvas-container'}
        sx={{
            ...styles.canvas,
            ...styles.canvasContainer,
            ...(lineupIsDragging && styles.canvasContainerDragOver)
        }} 
        ref={setNodeRef}
        data-droppable={isOver}
    >
        <PlayerDialog/>
        <TextDialog />
        {/* LOCKERROOM DISABLED FOR NOW  */}
        {/* <Box sx={styles.dataImageBox}>
            {!editor && dataImage ?
                <Image fit={'cover'} duration={500} src={dataImage} styles={styles.dataImage}/> : null}
        </Box> */}
        <canvas style={{visibility: editor ? 'visible' : 'hidden'}} ref={fabricRef} id="canvas"/>
    </Box>;
};

export default Canvas;